import React, { useMemo } from "react";
import { SurveyAnswerFn, SurveyAnswers } from "../../../../common/hooks/api/useSurvey";
import { OrganizationSurveyDto } from "../../../../common/service/server/dto/org/OrganizationSurveyDto";
import { prepareSurvey, questionsWithVisibility, SurveyDefinition } from "../../../surveys/survey";
import { SurveyQuestionContainer } from "./SurveyBooleanQuestion";
import { SurveyQuestion } from "./SurveyQuestion";

type SurveyRendererProps = {
  definition: SurveyDefinition;
  survey: OrganizationSurveyDto;
  remote: OrganizationSurveyDto;
  setAnswer: SurveyAnswerFn;
  offsetFirst?: boolean;
  skipCheckboxesAndVisibilityCheckForPrepareSurvey?: boolean;
};

export const SurveyRenderer: React.FC<SurveyRendererProps> = ({
  definition,
  survey,
  remote,
  setAnswer,
  skipCheckboxesAndVisibilityCheckForPrepareSurvey = false,
  ...rest
}) => {
  const localAnswers = useMemo<SurveyAnswers>(
    () => Object.fromEntries(Object.values(survey?.answers ?? {}).map((q) => [q.slug, q.answer])),
    [survey],
  );

  const remoteAnswers = useMemo<SurveyAnswers>(
    () => Object.fromEntries(Object.values(remote?.answers ?? {}).map((q) => [q.slug, q.answer])),
    [remote],
  );

  const questions = useMemo(() => {
    const showAllQueryParam = new URLSearchParams(window.location.search)
      .get("showAllQuestions")
      ?.toLowerCase();
    if (showAllQueryParam === "true" || showAllQueryParam === "1" || showAllQueryParam === "yes") {
      return questionsWithVisibility(definition, remoteAnswers);
    } else {
      return prepareSurvey(
        definition,
        remoteAnswers,
        /* showAll =*/ false,
        /* skipCheckboxesAndVisibilityCheckForPrepareSurvey = */ skipCheckboxesAndVisibilityCheckForPrepareSurvey,
      );
    }
  }, [definition, remoteAnswers]);

  return (
    <SurveyQuestionContainer {...rest}>
      {questions.map((q) => {
        let questionText: string | undefined = q.plaintextQuestion;
        if (!questionText && typeof q.question == "string") {
          questionText = q.question;
        }

        const loading = localAnswers[q.slug] != remoteAnswers[q.slug];
        return (
          <SurveyQuestion
            key={q.slug}
            question={q}
            answer={q.visible ? localAnswers[q.slug] : undefined}
            onAnswer={(ans) => setAnswer(q.slug, ans, questionText)}
            loading={loading}
          />
        );
      })}
    </SurveyQuestionContainer>
  );
};
